import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { Search02Icon } from '@hugeicons-pro/core-solid-rounded';
import { Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { FlashList } from '@shopify/flash-list';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';

import { HStack } from '@/components/gluestack/hstack';
import { CloseIcon, CheckIcon, Icon } from '@/components/gluestack/icon';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { Pressable } from '@/components/gluestack/pressable';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import {
  IContextSheetContentProps,
  ISheetConfig,
  DRAWER_SCROLL_CLASSES,
} from '@/components/ui/ContextSheet';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

export interface ISingleSelectOption {
  key: string;
  label: string;
}

export interface ISingleSelectSheetProps extends IContextSheetContentProps {
  options?: ISingleSelectOption[];
  selectedKey?: string | null;
  onSelectionChange?: (key: string | null) => void;
  searchable?: boolean;
  allowClear?: boolean;
  title?: string;
  renderOptionIcon?: (key: string) => React.ReactNode;
}

export default function SingleSelectSheet({
  onClose,
  payload,
  options: directOptions,
  selectedKey: directSelectedKey,
  onSelectionChange: directOnSelectionChange,
  searchable = true,
  allowClear = true,
}: ISingleSelectSheetProps & { payload?: Partial<ISingleSelectSheetProps> }) {
  const options = (payload?.options ?? directOptions ?? []) as ISingleSelectOption[];
  const selectedKey = payload?.selectedKey ?? directSelectedKey ?? null;
  const onSelectionChange = payload?.onSelectionChange ?? directOnSelectionChange;
  const renderOptionIcon = payload?.renderOptionIcon;
  const isLargeScreen = useIsLargeScreen();
  const gorhomFlashListScroll = useBottomSheetScrollableCreator();

  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [localSelection, setLocalSelection] = useState<string | null>(selectedKey);
  const selectionRef = useRef<string | null>(localSelection);
  const didCommitRef = useRef(false);

  const commitSelection = useCallback(() => {
    didCommitRef.current = true;
    onSelectionChange?.(selectionRef.current);
  }, [onSelectionChange]);

  useEffect(() => {
    selectionRef.current = localSelection;
  }, [localSelection]);

  useEffect(() => {
    return () => {
      // Commit selection when the sheet is dismissed (backdrop swipe, header close, etc.)
      if (!didCommitRef.current) {
        commitSelection();
      }
    };
  }, [commitSelection]);

  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    if (!searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, searchQuery]);

  const selectOption = useCallback((key: string) => {
    setLocalSelection(key);
  }, []);

  const handleDone = useCallback(() => {
    commitSelection();
    onClose?.();
  }, [commitSelection, onClose]);

  const handleClear = useCallback(() => {
    setLocalSelection(null);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: ISingleSelectOption }) => {
      const isSelected = localSelection === item.key;
      return (
        <Pressable
          onPress={() => selectOption(item.key)}
          className={`mx-1 flex-row items-center gap-3 rounded-xl px-3 py-3 active:bg-background-50 web:cursor-pointer web:hover:bg-background-50 ${
            isSelected ? 'bg-background-200' : ''
          }`}>
          <View
            className={`h-6 w-6 items-center justify-center rounded-full border-2 ${
              isSelected ? 'border-primary-500 bg-primary-500' : 'border-typography-300'
            }`}>
            {isSelected && <Icon as={CheckIcon} size="xs" className="color-white" />}
          </View>
          {renderOptionIcon?.(item.key)}
          <Text className={`flex-1 ${isSelected ? 'font-semibold' : ''}`}>{item.label}</Text>
        </Pressable>
      );
    },
    [localSelection, selectOption, renderOptionIcon]
  );

  const selectedLabel = useMemo(() => {
    if (!localSelection) return t('search.noneSelected', 'None selected');
    const option = options.find((o) => o.key === localSelection);
    return option?.label ?? localSelection;
  }, [localSelection, options, t]);
  const optionsList = (
    <FlashList<ISingleSelectOption>
      {...DRAWER_SCROLL_CLASSES}
      data={filteredOptions}
      renderItem={renderItem}
      keyExtractor={(item) => item.key}
      keyboardShouldPersistTaps="handled"
      {...(!isLargeScreen ? { renderScrollComponent: gorhomFlashListScroll } : {})}
    />
  );

  return (
    <VStack className="flex-1 gap-2">
      {searchable && options && options.length > 10 && (
        <Input variant="outline" className="mx-1">
          <InputSlot className="pl-3">
            <HugeIconsWrapper icon={Search02Icon} size={16} className="color-typography-700" />
          </InputSlot>
          <InputField
            placeholder={t('common.search', 'Search...')}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoFocus={false}
            autoCorrect={false}
            autoCapitalize="none"
            autoComplete={Platform.OS === 'web' ? ('off' as any) : undefined}
          />
          {searchQuery?.length ? (
            <InputSlot onPress={clearSearch} className="pr-3">
              <InputIcon as={CloseIcon} />
            </InputSlot>
          ) : null}
        </Input>
      )}

      <View className="min-h-[200px] flex-1">{optionsList}</View>

      <HStack className="items-center justify-between gap-2 border-t border-background-100 px-1 pt-3">
        <HStack className="items-center gap-2">
          {allowClear && localSelection && (
            <Pressable onPress={handleClear}>
              <Text size="sm" className="text-primary-500">
                {t('common.clear', 'Clear')}
              </Text>
            </Pressable>
          )}
          <Text size="sm" className="text-typography-500">
            {selectedLabel}
          </Text>
        </HStack>
        <ButtonWrapper
          icon={Tick02Icon}
          text={t('common.done', 'Done')}
          onPress={handleDone}
          size="sm"
        />
      </HStack>
    </VStack>
  );
}

SingleSelectSheet.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'sm',
  height: 'full',
  scrollableContent: true,
  integratedNativeSheetScrollGestures: true,
} satisfies ISheetConfig;
